/**
 * Eigenkontrollen aus der Sicht des Prüfbüros.
 *
 * Der Betreiber stellt fest, der Prüfer entscheidet. Diese Abfragen sind
 * die zweite Hälfte davon: was gemeldet wurde, und die Kenntnisnahme.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'

export type Kontrollart = 'sicht' | 'operativ'

export const KONTROLLARTTEXT: Record<Kontrollart, string> = {
  sicht: 'Visuelle Routinekontrolle',
  operativ: 'Operative Inspektion',
}

export type Beanstandung = {
  bezeichnung: string
  in_ordnung: boolean
  befund: string | null
  /** Bilder zu diesem Punkt. Mit ihnen lässt sich entscheiden, ob es eilt. */
  foto_ids: string[]
}

export type Kontrollgang = {
  id: string
  spielplatz_id: string
  spielplatz_name: string
  kunde_id: string
  kunde_name: string
  art: Kontrollart
  datum: string
  durchgefuehrt_von: string
  bemerkung: string | null
  hat_befund: boolean
  gesehen_am: string | null
  beanstandungen: Beanstandung[]
  anzahl_punkte: number
}

export function useKontrollgaenge(nurOffene = false, spielplatzId?: string) {
  const teile = new URLSearchParams()
  if (nurOffene) teile.set('nur_offene', 'true')
  if (spielplatzId) teile.set('spielplatz_id', spielplatzId)
  const anhang = teile.toString() ? `?${teile}` : ''

  return useQuery({
    queryKey: ['eigenkontrollen', nurOffene, spielplatzId ?? null] as const,
    queryFn: ({ signal }) =>
      api.holen<Kontrollgang[]>(`/api/eigenkontrollen${anhang}`, signal),
  })
}

export function useKontrollgangGesehen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.senden<Kontrollgang>(`/api/eigenkontrollen/${id}/gesehen`, {}),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['eigenkontrollen'] }),
  })
}

export function useKontrollgangLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/eigenkontrollen/${id}`),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['eigenkontrollen'] }),
  })
}
